import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap } from '@vis.gl/react-google-maps'
import Supercluster from 'supercluster'
import { MapMarker } from '@/components/MapMarker'
import { MapZoomControls } from '@/components/MapPlaceholder'
import type { PortfolioProperty } from '@/types/api'

interface PortfolioMapProps {
	apiKey: string
	/** Falls back to Google's demo Map ID, which AdvancedMarker requires. */
	mapId?: string
	properties: PortfolioProperty[]
	/** Matching available unit count per Engrain asset ID; properties without a count are hidden. */
	counts: Record<string, number>
	onPropertyClick?: (engrainAssetId: string) => void
	/** Called with the visible area whenever the map settles after a pan or zoom. */
	onViewportChange?: (viewport: MapViewport) => void
	/** Camera to open at instead of fitting the matches, e.g. to come back where the user left off. */
	initialCamera?: MapCamera
	/** Bumping this refits the map to the matching properties. */
	resetSignal?: number
	className?: string
}

type LocatedProperty = PortfolioProperty & { lat: number; lng: number }
export interface Bounds { north: number; south: number; east: number; west: number }
interface LatLng { lat: number; lng: number }
export interface MapCamera { center: LatLng; zoom: number }
export interface MapViewport extends MapCamera { bounds: Bounds }
type PropertyPoint = { property: LocatedProperty; units: number }
type ClusterUnits = { units: number }

const SINGLE_PROPERTY_ZOOM = 14
const BOUNDS_PADDING = 64
const CLUSTER_RADIUS = 60
// A market fits in one view, so clustering the whole world avoids re-querying on pan.
const WORLD_BBOX: [number, number, number, number] = [-180, -85, 180, 85]
const MARKER_FOCUS_TIMEOUT = 5000

/**
 * Focuses a property's marker once Google renders it, unless focus has moved off `placeholder`.
 * Returns a cleanup that stops waiting.
 */
export function focusPortfolioMarkerWhenReady (engrainAssetId: string, placeholder: Element | null): () => void {
	const root = document.querySelector('.spaces-portfolio-map')
	if (!root) return () => {}
	const selector = `[data-property="${CSS.escape(engrainAssetId)}"]`
	const observer = new MutationObserver(tryFocus)
	const timer = window.setTimeout(stop, MARKER_FOCUS_TIMEOUT)

	function stop () {
		observer.disconnect()
		window.clearTimeout(timer)
	}

	function tryFocus () {
		const active = document.activeElement
		if (active !== placeholder && active !== document.body) return stop()
		// Google puts tabindex/role=button on the marker host, not on our content.
		const marker = root?.querySelector(selector)?.closest<HTMLElement>('gmp-advanced-marker[tabindex]')
		if (!marker) return
		marker.focus()
		stop()
	}

	observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex'] })
	tryFocus()
	return stop
}

/** Whether a point lies inside map bounds, including bounds that cross the antimeridian. */
export function isWithinBounds ({ lat, lng }: LatLng, bounds: Bounds): boolean {
	if (lat < bounds.south || lat > bounds.north) return false
	return bounds.west <= bounds.east
		? lng >= bounds.west && lng <= bounds.east
		: lng >= bounds.west || lng <= bounds.east
}

function getBounds (points: LatLng[]): Bounds | null {
	if (points.length < 2) return null
	return {
		north: Math.max(...points.map(p => p.lat)),
		south: Math.min(...points.map(p => p.lat)),
		east: Math.max(...points.map(p => p.lng)),
		west: Math.min(...points.map(p => p.lng))
	}
}

function fitPoints (map: google.maps.Map, points: LatLng[]) {
	const bounds = getBounds(points)
	if (bounds) {
		map.fitBounds(bounds, BOUNDS_PADDING)
	} else if (points.length === 1) {
		map.setCenter(points[0])
		map.setZoom(SINGLE_PROPERTY_ZOOM)
	}
}

function unitsLabel (units: number): string {
	return `${units} ${units === 1 ? 'unit' : 'units'} available`
}

/** The marker element is the focusable button, so it carries the name; `title` alone is only a tooltip. */
function labelMarker (label: string) {
	return (marker: google.maps.marker.AdvancedMarkerElement | null) => {
		marker?.setAttribute('aria-label', label)
	}
}

function MapFallback () {
	return (
		<div className='spaces:flex spaces:items-center spaces:justify-center spaces:w-full spaces:h-full'>
			<p className='spaces-portfolio-map-fallback spaces:text-14/24 spaces:font-default spaces:text-content-3 spaces:p-16 spaces:text-center'>
				Map unavailable.
			</p>
		</div>
	)
}

let mapAuthFailed = false
const authFailureListeners = new Set<() => void>()
let authFailureHookInstalled = false

function onMapAuthFailure (listener: () => void): () => void {
	if (!authFailureHookInstalled) {
		authFailureHookInstalled = true
		const hostHandler = window.gm_authFailure
		window.gm_authFailure = () => {
			mapAuthFailed = true
			authFailureListeners.forEach(notify => notify())
			hostHandler?.()
		}
	}
	authFailureListeners.add(listener)
	return () => { authFailureListeners.delete(listener) }
}

/** Google Maps errors (e.g. an unauthorized key) must not unmount the whole app. */
class MapErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
	state = { failed: mapAuthFailed }
	private unsubscribe?: () => void

	static getDerivedStateFromError () {
		return { failed: true }
	}

	// Google's cleanup throws after an auth failure; unmounting here keeps it inside this boundary.
	componentDidMount () {
		this.unsubscribe = onMapAuthFailure(() => { this.setState({ failed: true }) })
	}

	componentWillUnmount () {
		this.unsubscribe?.()
	}

	render () {
		return this.state.failed ? <MapFallback /> : this.props.children
	}
}

function PortfolioZoomControls ({ properties }: { properties: LocatedProperty[] }) {
	const map = useMap()

	const zoomBy = useCallback((delta: number) => {
		if (!map) return
		map.setZoom((map.getZoom() ?? SINGLE_PROPERTY_ZOOM) + delta)
	}, [map])

	const reset = useCallback(() => {
		if (map) fitPoints(map, properties)
	}, [map, properties])

	return (
		<MapZoomControls
			onZoomIn={() => zoomBy(1)}
			onZoomOut={() => zoomBy(-1)}
			onReset={reset}
			className='spaces:absolute spaces:right-12 spaces:bottom-12 spaces:z-[1]'
		/>
	)
}

/** Refits the map when filtering changes which properties have matches; the initial fit comes from `defaultBounds`. */
function FitOnFilterChange ({ properties }: { properties: LocatedProperty[] }) {
	const map = useMap()
	const signature = properties.map(p => p.engrainAssetId).join(',')
	const fittedRef = useRef<string | null>(null)

	useEffect(() => {
		if (!map || fittedRef.current === signature) return
		const isInitial = fittedRef.current === null
		fittedRef.current = signature
		if (!isInitial) fitPoints(map, properties)
	}, [map, signature, properties])

	return null
}

/** Refits the map to `properties` each time `signal` changes after mount. */
function FitOnSignal ({ properties, signal }: { properties: LocatedProperty[]; signal: number }) {
	const map = useMap()
	const lastSignalRef = useRef(signal)

	useEffect(() => {
		if (!map || lastSignalRef.current === signal) return
		lastSignalRef.current = signal
		fitPoints(map, properties)
	}, [map, signal, properties])

	return null
}

function ReportViewport ({ onViewportChange }: { onViewportChange: (viewport: MapViewport) => void }) {
	const map = useMap()
	const callbackRef = useRef(onViewportChange)
	callbackRef.current = onViewportChange

	useEffect(() => {
		if (!map) return
		const listener = map.addListener('idle', () => {
			const bounds = map.getBounds()
			const center = map.getCenter()
			const zoom = map.getZoom()
			if (bounds && center && zoom !== undefined) {
				callbackRef.current({ bounds: bounds.toJSON(), center: center.toJSON(), zoom })
			}
		})
		return () => { listener.remove() }
	}, [map])

	return null
}

function useIntegerZoom (map: google.maps.Map | null): number | null {
	const [zoom, setZoom] = useState<number | null>(null)

	useEffect(() => {
		if (!map) return
		const sync = () => {
			const current = map.getZoom()
			if (current !== undefined) setZoom(Math.floor(current))
		}
		sync()
		const listener = map.addListener('zoom_changed', sync)
		return () => { listener.remove() }
	}, [map])

	return zoom
}

interface ClusteredMarkersProps {
	properties: LocatedProperty[]
	counts: Record<string, number>
	onPropertyClick?: (engrainAssetId: string) => void
}

function ClusteredMarkers ({ properties, counts, onPropertyClick }: ClusteredMarkersProps) {
	const map = useMap()
	const zoom = useIntegerZoom(map)

	const index = useMemo(() => {
		const cluster = new Supercluster<PropertyPoint, ClusterUnits>({
			radius: CLUSTER_RADIUS,
			map: point => ({ units: point.units }),
			reduce: (total, point) => { total.units += point.units }
		})
		cluster.load(properties.map(property => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [property.lng, property.lat] },
			properties: { property, units: counts[property.engrainAssetId] }
		})))
		return cluster
	}, [properties, counts])

	const features = useMemo(() => zoom === null ? [] : index.getClusters(WORLD_BBOX, zoom), [index, zoom])

	const expandCluster = useCallback((clusterId: number) => {
		if (!map) return
		fitPoints(map, index.getLeaves(clusterId, Infinity).map(leaf => leaf.properties.property))
	}, [map, index])

	return (
		<>
			{features.map(feature => {
				const [lng, lat] = feature.geometry.coordinates
				if ('cluster' in feature.properties) {
					const { cluster_id: clusterId, point_count: pointCount, units } = feature.properties
					const label = `${pointCount} properties, ${unitsLabel(units)}`
					return (
						<AdvancedMarker
							key={`cluster-${clusterId}`}
							ref={labelMarker(label)}
							position={{ lat, lng }}
							title={label}
							anchorLeft='-50%'
							anchorTop='-50%'
							zIndex={1}
							clickable
							onClick={() => expandCluster(clusterId)}
						>
							<MapMarker value={units} data-cluster={pointCount} />
						</AdvancedMarker>
					)
				}
				const { property, units } = feature.properties
				const label = `${property.name}, ${unitsLabel(units)}`
				return (
					<AdvancedMarker
						key={property.engrainAssetId}
						ref={labelMarker(label)}
						position={{ lat, lng }}
						title={label}
						anchorLeft='-50%'
						anchorTop='-50%'
						zIndex={1}
						clickable
						onClick={() => onPropertyClick?.(property.engrainAssetId)}
					>
						<MapMarker
							value={units}
							status='inactive'
							data-property={property.engrainAssetId}
						/>
					</AdvancedMarker>
				)
			})}
		</>
	)
}

export function PortfolioMap ({ apiKey, mapId, properties, counts, onPropertyClick, onViewportChange, initialCamera, resetSignal = 0, className = '' }: PortfolioMapProps) {
	const located = useMemo(
		() => properties.filter((p): p is LocatedProperty => p.lat !== null && p.lng !== null),
		[properties]
	)
	const matching = useMemo(() => located.filter(p => (counts[p.engrainAssetId] ?? 0) > 0), [located, counts])
	// With no matches, frame the whole market rather than leaving the view where it was.
	const framed = matching.length > 0 ? matching : located
	const bounds = useMemo(() => getBounds(framed), [framed])
	const containerClass = `spaces-map spaces-portfolio-map spaces:relative spaces:bg-canvas-base spaces:rounded-24 spaces:border spaces:border-canvas-3 spaces:overflow-hidden spaces:min-h-[400px] ${className}`

	if (!apiKey || located.length === 0) {
		return (
			<div className={containerClass}>
				<MapFallback />
			</div>
		)
	}

	return (
		<div className={containerClass}>
			<MapErrorBoundary>
				<APIProvider apiKey={apiKey}>
					<GoogleMap
						mapId={mapId || 'DEMO_MAP_ID'}
						defaultBounds={bounds && !initialCamera ? { ...bounds, padding: BOUNDS_PADDING } : undefined}
						defaultCenter={initialCamera?.center ?? (bounds ? undefined : { lat: framed[0].lat, lng: framed[0].lng })}
						defaultZoom={initialCamera?.zoom ?? (bounds ? undefined : SINGLE_PROPERTY_ZOOM)}
						disableDefaultUI
						clickableIcons={false}
						gestureHandling='cooperative'
						className='spaces:w-full spaces:h-full'
					>
						<ClusteredMarkers
							properties={matching}
							counts={counts}
							onPropertyClick={onPropertyClick}
						/>
						<FitOnFilterChange properties={framed} />
						<FitOnSignal properties={framed} signal={resetSignal} />
						{onViewportChange && <ReportViewport onViewportChange={onViewportChange} />}
					</GoogleMap>
					<PortfolioZoomControls properties={framed} />
				</APIProvider>
			</MapErrorBoundary>
		</div>
	)
}
